import React, { useState, useEffect, useMemo } from 'react';
import { Eye, Trash, Upload, FileText, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import API_URL from '../../config';
import { useAuth } from '../../AuthContext';
import PdfViewerModal from './PdfViewerModal';

const ManagerPayslipView = () => {
  const [users, setUsers] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);
  const [uploadingState, setUploadingState] = useState({});
  const [message, setMessage] = useState({ text: '', type: '' });
  const [viewState, setViewState] = useState({ isOpen: false, url: null, title: '' });
  const { logout } = useAuth();

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const year = selectedMonth.split('-')[0];

      const [usersRes, payslipsRes] = await Promise.all([
        fetch(`${API_URL}/users/`, { headers }),
        fetch(`${API_URL}/payslips/?year=${year}`, { headers })
      ]);

      if (usersRes.status === 401 || payslipsRes.status === 401) {
          logout();
          return;
      }

      if (usersRes.ok && payslipsRes.ok) {
        const usersData = await usersRes.json();
        const payslipsData = await payslipsRes.json();
        setUsers(usersData);
        setPayslips(payslipsData);
      } else {
        setMessage({ text: 'Erreur lors du chargement des données', type: 'error' });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setMessage({ text: 'Erreur réseau', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, logout]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const userRows = useMemo(() => {
    return users.map(user => {
      const payslip = payslips.find(p => 
        p.user_id === user.id && 
        p.period_start.startsWith(selectedMonth)
      );
      return { user, payslip };
    });
  }, [users, payslips, selectedMonth]);

  const handleUpload = async (userId, file) => {
    if (!file) return;

    setUploadingState(prev => ({ ...prev, [userId]: true }));
    setMessage({ text: '', type: '' });

    try {
      const token = localStorage.getItem('token');
      const [year, monthNum] = selectedMonth.split('-');
      const periodStart = `${year}-${monthNum}-01`;
      const lastDay = new Date(year, monthNum, 0).getDate();
      const periodEnd = `${year}-${monthNum}-${lastDay}`;

      const formData = new FormData();
      formData.append('user_id', userId);
      formData.append('period_start', periodStart);
      formData.append('period_end', periodEnd);
      formData.append('file', file);

      const response = await fetch(`${API_URL}/payslips/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        setMessage({ text: 'Fiche de paie envoyée avec succès', type: 'success' });
        fetchData();
      } else {
        const err = await response.json().catch(() => ({}));
        setMessage({ text: err.detail || 'Erreur lors de l\'envoi', type: 'error' });
      }
    } catch (error) {
      console.error('Error uploading:', error);
      setMessage({ text: 'Erreur réseau', type: 'error' });
    } finally {
      setUploadingState(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleDelete = async (payslipId) => {
    if (!window.confirm('Supprimer cette fiche de paie ?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/payslips/${payslipId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setPayslips(prev => prev.filter(p => p.id !== payslipId));
        setMessage({ text: 'Supprimé', type: 'success' });
      } else {
        setMessage({ text: 'Erreur suppression', type: 'error' });
      }
    } catch (error) {
       console.error(error);
       setMessage({ text: 'Erreur réseau', type: 'error' });
    }
  };

  const handleView = async (id) => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/payslips/${id}/download`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
  
        if (response.ok) {
          const data = await response.json();
          setViewState({ 
            isOpen: true, 
            url: data.download_url, 
            title: `Fiche de paie` 
          });
        }
      } catch (error) {
        console.error('Error viewing payslip:', error);
      }
    };

  const formatMonthYear = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, month - 1);
    return new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(date);
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-0">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-4 sm:p-6">
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-800">Gestion des Fiches de Paie</h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">Vue d'ensemble et import mensuel</p>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            <label className="text-sm font-medium text-gray-700">Période :</label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="block w-full sm:w-48 border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>
      </div>

      {/* Messages */}
      {message.text && (
        <div className={`p-3 sm:p-4 rounded-md text-sm ${message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {message.text}
        </div>
      )}

      {/* Content */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
             <div className="p-8 sm:p-12 text-center text-gray-500">Chargement des données...</div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employé</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut ({formatMonthYear(selectedMonth)})</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fichier</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {userRows.map(({ user, payslip }) => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold">
                            {user.full_name.charAt(0)}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        {payslip ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Disponible
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            Manquant
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {payslip ? (
                          <div className="flex items-center text-gray-700">
                            <FileText className="w-4 h-4 mr-2" />
                            <span className="truncate max-w-xs">{payslip.file_name}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">--</span>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {payslip ? (
                          <div className="flex justify-end items-center space-x-3">
                             <button
                              onClick={() => handleView(payslip.id)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Voir"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(payslip.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Supprimer"
                            >
                              <Trash className="w-5 h-5" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end">
                            <input
                              type="file"
                              id={`upload-${user.id}`}
                              className="hidden"
                              accept=".pdf"
                              onChange={(e) => handleUpload(user.id, e.target.files[0])}
                              disabled={uploadingState[user.id]}
                            />
                            <label
                              htmlFor={`upload-${user.id}`}
                              className={`cursor-pointer inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none ${uploadingState[user.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {uploadingState[user.id] ? (
                                <Loader className="w-4 h-4 animate-spin mr-1" />
                              ) : (
                                <Upload className="w-4 h-4 mr-1" />
                              )}
                              {uploadingState[user.id] ? 'Envoi...' : 'Ajouter'}
                            </label>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {userRows.length === 0 && (
                     <tr>
                        <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                          Aucun employé trouvé.
                        </td>
                     </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-200">
              {userRows.map(({ user, payslip }) => (
                <div key={user.id} className="p-4 hover:bg-gray-50 transition-colors">
                  {/* User Info */}
                  <div className="flex items-start space-x-3 mb-3">
                    <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-lg shrink-0">
                      {user.full_name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{user.full_name}</div>
                      <div className="text-xs text-gray-500 truncate">{user.email}</div>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="mb-3">
                    {payslip ? (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Disponible - {formatMonthYear(selectedMonth)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Manquant - {formatMonthYear(selectedMonth)}
                      </span>
                    )}
                  </div>

                  {/* File Name */}
                  {payslip && (
                    <div className="flex items-center text-xs text-gray-600 mb-3">
                      <FileText className="w-3 h-3 mr-1 shrink-0" />
                      <span className="truncate">{payslip.file_name}</span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    {payslip ? (
                      <>
                        <button
                          onClick={() => handleView(payslip.id)}
                          className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-indigo-600 text-sm font-medium rounded-md text-indigo-600 bg-white hover:bg-indigo-50 focus:outline-none"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Voir
                        </button>
                        <button
                          onClick={() => handleDelete(payslip.id)}
                          className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-red-600 text-sm font-medium rounded-md text-red-600 bg-white hover:bg-red-50 focus:outline-none"
                        >
                          <Trash className="w-4 h-4 mr-1" />
                          Supprimer
                        </button>
                      </>
                    ) : (
                      <>
                        <input
                          type="file"
                          id={`upload-mobile-${user.id}`}
                          className="hidden"
                          accept=".pdf"
                          onChange={(e) => handleUpload(user.id, e.target.files[0])}
                          disabled={uploadingState[user.id]}
                        />
                        <label
                          htmlFor={`upload-mobile-${user.id}`}
                          className={`flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none ${uploadingState[user.id] ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          {uploadingState[user.id] ? (
                            <>
                              <Loader className="w-4 h-4 animate-spin mr-2" />
                              Envoi en cours...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Ajouter la fiche
                            </>
                          )}
                        </label>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {userRows.length === 0 && (
                <div className="p-8 text-center text-gray-500 text-sm">
                  Aucun employé trouvé.
                </div>
              )}
            </div>
          </>
        )}
      </div>

       {viewState.isOpen && (
        <PdfViewerModal
          isOpen={viewState.isOpen}
          onClose={() => setViewState({ ...viewState, isOpen: false })}
          url={viewState.url}
          title={viewState.title}
        />
      )}
    </div>
  );
};

export default ManagerPayslipView;
